import Image from '../components/common/Image';
import VectorImage from '../components/common/VectorImage';
import utils from '../../src/helpers/utils';
import classNames from 'classnames';
import {Fragment} from 'react';
import {REIMAGINE_IMAGES} from '../constants/reimagineImages';

// Parameters for renderImage function.
type RenderImageParams = {
  imageName: string; // name of the image file
  dataTestId?: string;
  classes?: string;
  imageWrapperClasses?: string;
  height?: number;
  width?: number;
  whiteLabelClient: string; // Identifies white label client associated with the current user. Used to select partner-specific image.
};

type RenderVectorImageParams = {
  imageName: string;
  imagePath: string;
  dataTestId: string;
  whiteLabelClient?: string;
};

type RenderReimagineIconParams = {
  iconName: string; // Reimagine path from REIMAGINE_IMAGES, with or without a leading `/`
  whiteLabelClient: string | null; // Identifies the white label client; used to select partner-specific image.
  classes?: string; // sizing/layout classes, e.g. 'h-6 w-6'
  colorClass?: string; // ODS background token used to tint the icon (e.g. 'bg-accent'); requires width AND height in `classes`.
  alt?: string;
  dataTestId?: string;
  useCache?: boolean;
};

type RenderErrorOutlineIconParams = {
  isReimagineThemeEligible: boolean;
  whiteLabelClient: string | null;
  classes: string; // Shared layout/color classes (without `material-icons`); applied to both variants.
  alt?: string;
  dataTestId?: string;
};

export default class renderUtils {
  /**
   * Returns a JSX element that displays a list of data elements along with an "Add" button if appropriate
   * @param {Array} data - array of data to display
   * @param {Function} renderItemFxn - function to render the data element
   * @param {JSX.Element} addButtonElement - optional: as per the latest design updates to monitoredInfo, renders "Add" button element to the bottom of the modal
   * @param {String} currentEditState - currentEdit state variable
   * @param {Number} maxCount - max data items allowed
   * @param {String} listStyle - optional: string of css class names to style the list div. Default ''.
   * @param {Number} indexModifier - optional: added to the renderItemFxn index param, subtracted from the maxCount addButton conditional display statement. Default 0.
   * @param {String} editStateCompare - optional: value to be compared to the currentEdit state in order to display addButton. Default null.
   * @param {JSX.Element} zeroStateElement - optional: element displayed if add button shouldn't be shown. Default null.
   * @param {String} addBtnPosition - optional: determines the render position of the add button, if null to the bottom of the modal
   * @return {JSX.Element}
   */
  static renderSupplemental(
    data: Array<$TSFixMe>,
    renderItemFxn: $TSFixMeFunction,
    currentEditState: string | number | null,
    maxCount: number,
    listStyle = '',
    indexModifier = 0,
    editStateCompare: string | null = null,
    zeroStateElement: JSX.Element | null = null,
    addButtonElement?: JSX.Element,
    addBtnPosition?: string
  ) {
    if (!Array.isArray(data)) {
      return null;
    }

    const supplementalList = data.map((supplementalRecord, i) => (
      <Fragment key={i}>{renderItemFxn(supplementalRecord, i + indexModifier)}</Fragment>
    ));

    const renderAddElement = () => {
      // addBtnPosition can be undefined to determine the position of add btn at the bottom of the modal
      return addBtnPosition ? addButtonElement : null;
    };

    return (
      <div className={listStyle}>
        {supplementalList}
        {currentEditState === editStateCompare && data.length < maxCount - indexModifier
          ? renderAddElement()
          : zeroStateElement}
        {currentEditState === 'add' ? renderItemFxn({}) : null}
      </div>
    );
  }
  /**
   * Returns a JSX element for a monitored-info row label (or similar static copy).
   * Text wraps within the label column so localized strings are not clipped with an ellipsis by default.
   * @param {String} fieldVal - field value (can be record label/value)
   * @param {String} classList - list of tailwind classes
   * @param {String} dataTestId - data-testid for testing purposes
   * @return {JSX.Element}
   */

  static renderMonitoredInfoTruncatedField(fieldVal: any, classList: string, dataTestId = '') {
    return (
      <div className={classList}>
        <div
          className="reimagine-text-body1 w-full min-w-0 break-words text-left"
          title={fieldVal}
          data-testid={dataTestId}
        >
          {fieldVal}
        </div>
      </div>
    );
  }

  /**
   * Renders image from CDN.
   */
  static renderImage({
    imageName,
    dataTestId,
    classes,
    imageWrapperClasses,
    height,
    width,
    whiteLabelClient,
  }: RenderImageParams): JSX.Element {
    const cdnPath = utils.getCdnImagesPath(`/dsp-northstar/${imageName}`, whiteLabelClient);
    return (
      <Image
        url={cdnPath}
        alt={imageName}
        classes={classes}
        imageWrapperClasses={imageWrapperClasses}
        height={height}
        width={width}
        dataTestId={dataTestId}
      />
    );
  }

  /**
   * Renders merchant image
   * @param {string} imageUrl - url of merchant image
   * @param {string} merchantName - merchant name to show fallback image if imageUrl is empty
   * @param {string} classes - class names for the image
   * @returns {JSX.Element} returns merchant image or logo with first letter from merchant name
   */
  static renderMerchantLogo(imageUrl: string, merchantName: string, classes?: string) {
    if (imageUrl) {
      return <img className={classes ?? 'h-8 w-8 rounded'} src={imageUrl} alt={merchantName} />;
    }

    let merchantFirstLetter = '';
    if (merchantName) {
      merchantFirstLetter = merchantName[0];
    }

    /** Caller-provided `classes` set the box (e.g. h-8 w-10, rounded-full). Avoid default lg: sizing so fixed slots (e.g. overlapping avatars) are not clipped. */
    const fallbackBoxClass = classNames(
      'flex shrink-0 items-center justify-center bg-onBackground-extraLight',
      classes || 'h-8 w-8 rounded'
    );

    return (
      <div className={fallbackBoxClass}>
        <span className="reimagine-text-h4 text-sm font-bold leading-none text-onSurface-primary">
          {merchantFirstLetter}
        </span>
      </div>
    );
  }

  /**
   * Renders image from CDN.
   */
  static renderVectorImage({
    imagePath,
    imageName,
    dataTestId,
    whiteLabelClient = '',
  }: RenderVectorImageParams): JSX.Element {
    const cdnPath = utils.getCdnImagesPath(imagePath, whiteLabelClient);
    return <VectorImage url={cdnPath} alt={imageName} dataTestId={dataTestId} />;
  }

  /**
   * Returns a VectorImage for a Reimagine (Norton ODS) SVG icon, resolved from just the icon
   * basename via `utils.getCdnReimagineImagesPath`. Optionally tints the (single-color) icon with an
   * ODS color token by passing `colorClass` (e.g. 'bg-accent'), which requires an
   * explicit width AND height in `classes`.
   *
   * @param {RenderReimagineIconParams} params - icon name, white label client, and optional styling.
   * @return {JSX.Element}
   */
  static renderReimagineIcon({
    iconName,
    whiteLabelClient,
    classes,
    colorClass,
    alt,
    dataTestId,
    useCache = true,
  }: RenderReimagineIconParams): JSX.Element {
    const normalizedIconPath = iconName.startsWith('/') ? iconName : `/${iconName}`;
    const cdnPath = utils.getCdnReimagineImagesPath(normalizedIconPath, whiteLabelClient);
    return (
      <VectorImage
        url={cdnPath}
        alt={alt ?? iconName}
        classes={classes}
        maskColorClass={colorClass}
        dataTestId={dataTestId}
        useCache={useCache}
      />
    );
  }

  /**
   * Renders the error/attention icon: Reimagine `ic_simple_status_attention` when eligible,
   * otherwise the legacy Material `error_outline` glyph. Pass shared sizing/color classes
   * without `material-icons` — that class is added only on the Material fallback.
   */
  static renderErrorOutlineIcon({
    isReimagineThemeEligible,
    whiteLabelClient,
    classes,
    alt,
    dataTestId,
  }: RenderErrorOutlineIconParams): JSX.Element {
    if (isReimagineThemeEligible) {
      return renderUtils.renderReimagineIcon({
        iconName: REIMAGINE_IMAGES.icSimpleStatusAttention,
        whiteLabelClient,
        classes,
        alt,
        dataTestId,
      });
    }
    return (
      // reimagine-ignore — material icon glyph, not themed text
      <i className={classNames('material-icons', classes)}>error_outline</i>
    );
  }

  /**
   * renders error section
   * @param {StringMap} stringBundle - string bundle
   * @param {string} whiteLabelClient - Identifies the white label client associated with the current user.
   * @param {boolean} isReimagineThemeEligible - whether the current theme is reimagine theme
   * @returns {JSX.Element} returns error screen
   */
  static renderTxmErrorSection(stringBundle: StringMap, whiteLabelClient: string, isReimagineThemeEligible: boolean) {
    const cdnPath = utils.getThemedCdnImagesPath(
      isReimagineThemeEligible,
      whiteLabelClient,
      REIMAGINE_IMAGES.alertTriangle,
      '/dsp-northstar/transaction-monitoring-error'
    );

    return (
      <div className="mb-5 flex flex-col-reverse justify-between md:mb-10 md:flex-row">
        <div className="mt-2 flex w-full flex-col md:w-1/2">
          <div
            className="reimagine-text-h3 mb-2 text-center text-xl font-bold md:text-left md:text-2.5xl md:font-extrabold"
            data-testid="we-are-sorry"
          >
            {stringBundle.WERE_SORRY}
          </div>
          <div className="reimagine-text-body1 indent-4 leading-6 md:indent-0" data-testid="we-are-having-trouble">
            {stringBundle.WERE_HAVING_TROUBLE}
          </div>
        </div>
        <div className="w-full md:w-1/2">
          <div className="flex justify-center" data-testid="transaction-monitoring-error-image">
            <VectorImage url={cdnPath} alt="transaction-monitoring-error" classes="w-70 h-50" useCache={true} />
          </div>
        </div>
      </div>
    );
  }
}
